import { subtract, dot, norm, add, multiply, divide } from 'mathjs'
import { getUsersPosition } from './map/getUsersPosition.js';
import { categoriesHeights } from './map/polygonHeights.js';
import { sortStages } from './utils.js';

export const findEntry = (day, area) => {
    let selectedEntry = { 
                        id: 0,
                        attributes: {
                            time_pos: 0
                        }    
                    }
    for (const id in area.entries) {
        if (area.entries[id].attributes.time_pos <= day && 
            area.entries[id].attributes.time_pos >= selectedEntry.attributes.time_pos) {
            selectedEntry = area.entries[id]
        }
    }
    return selectedEntry
}

export const findArea = (areaName, projectData) => {
    let selectedArea = ''
    for (const area of Object.values(projectData.areas)) {
        if (area.attributes.name === areaName) {
            selectedArea = area
        }
    }
    return selectedArea
}

export const findMainArea = (areas) => {
    for (var id in areas) {
        if (areas[id].attributes.main) {
            return areas[id]
        }
    }
    return 'unset'
}

export const findFirstLevel = (projectData) => {
    for (var id in projectData.levels) {
        return projectData.levels[id]
    }
}

export const findCards = (day, projectData, position=null) => {
    let cardsGroups = projectData.cards_groups
	let selectedCards = []
    for (const id in cardsGroups) {
        let selectedCard = findCardFromCardsGroup(day, cardsGroups[id], position)
        if (selectedCard) {
            selectedCard = {
                ...selectedCard,
                index: cardsGroups[id].attributes.index
            }
            selectedCards.push(selectedCard)
        }
    }
	return selectedCards
}

export const findCardFromCardsGroup = (day, cardsGroup, position) => {
    let bestCard = null;
    for (const card of Object.values(cardsGroup.cards)) {
        if (position && position !== 'Todo') {
            let positions = getUsersPosition(card.user, card.users_in_charge)
            if (!positions.includes(position)) continue
        }
        let daysDiff = parseInt(day) - parseInt(card.attributes.time_pos)
        if (daysDiff >= 0) {
            if (!bestCard) {
                bestCard = card
            } else if (bestCard.id < card.id) {
                bestCard = card
            }
        }
    }
    return bestCard
}

export const findCardById = (id, projectData) => {
    for (const card of projectData.cards) {
        if (card.id === id) {
            return card
        }
    }
    return null
}

export const findLevel = (levelName, projectData) => {
	let selectedLevel = ''
	for (const level of projectData.levels) {
		if (level.attributes.name === levelName) {
			selectedLevel = level
		}
	}
	return selectedLevel
}

const createVectorFromTrajectory = (trajectory) => {
    return [trajectory.attributes.longitude, trajectory.attributes.latitude]
}

const getDistanceAndProjection = (longitude, latitude, trajectory1, trajectory2) => {
    const A = createVectorFromTrajectory(trajectory1)
    const B = createVectorFromTrajectory(trajectory2)
    const M = subtract(B, A)
    const len = norm(M)
    const P = [longitude, latitude]
    const D = subtract(P, A)
    const MAbs = dot(M, M)
    const t0Num = dot(D, M)
    const t0 = divide(t0Num, MAbs)
    if (t0 <= 0) {
        return [norm(subtract(P, A)), A, 0, len]
    } else if (t0 < 1) {
        const L = add(A, multiply(t0, M))
        return [norm(subtract(P, L)), L, t0, len]
    } else {
        return [norm(subtract(P, B)), B, 1, len]
    }
}

export const findClosestFrame = (longitude, latitude, entry) => {
    let allDistancesAndProjections = []
    let totalLen = 0
    if (Object.keys(entry.trajectories).length > 1) {
        for (var i = 0; i < Object.keys(entry.trajectories).length - 1; i++) {
            const [distance, projection, t0, len] = getDistanceAndProjection(longitude, 
                                                                    latitude, 
                                                                    Object.values(entry.trajectories)[i], 
                                                                    Object.values(entry.trajectories)[i+1])
            allDistancesAndProjections.push([distance, projection, len*t0, totalLen])
            totalLen += len
        }
    allDistancesAndProjections.sort(function (a, b) {
        return a[0] - b[0]
    })
    return Math.round((parseFloat(allDistancesAndProjections[0][2] + allDistancesAndProjections[0][3])/totalLen)*entry.attributes.nr_of_images)
    } else {
        return null
    }
}

export const findItemProgress = (itemId, itemsProgress) => {
    let selectedProgress
    for (var [id, progress] of Object.entries(itemsProgress.items_progress)) {
        if (itemId.toString() === id) {
            selectedProgress = progress
            return selectedProgress
        }
    }
    return selectedProgress
}

export const findProtocolGroup = (protocolId, protocolGroups) => {
    protocolId = protocolId.toString()
    for (const protocolGroup of Object.values(protocolGroups)) {
        if (Object.keys(protocolGroup.protocols).includes(protocolId)){
            return protocolGroup
        }
    }
    return null
}

export const findProtocolName = (protocolId, protocolgroups) => {
    protocolId = protocolId.toString()
    for (const protocolgroup of Object.values(protocolgroups)) {
        if (Object.keys(protocolgroup.protocols).includes(protocolId)){
            let protocol = protocolgroup.protocols[protocolId]
            return protocol.attributes.name
        }
    }
    return ''
}

export const findAnswer = (answers, questionId, questionoptionId=null) => {
    for (const answer of answers) {
        if (answer.attributes.question_id === questionId
            && answer.attributes.questionoption_id === questionoptionId) {
            return answer
        }
    }
    return null
}

export const isMatrixProtocol = (answers) => {
    for (const answer of answers) {
        if (answer.attributes.questionoption_id) {
            return true
        }
    }
    return false
}

export const findFirstLevelOfStage = (stageId, levelsData) => {
    for (const levelData of Object.values(levelsData)) {
        if (levelData.attributes.stage_id === stageId) {
            return levelData
        }
    }
    return null

}

export function findFirstStageId(projectData) {
    if (projectData.stages) {
        const sortedStages = sortStages(Object.values(projectData.stages));
        return parseInt(sortedStages[0].id);
    }
    return 0;
}

export function findFirstLevelIdOfStage(levelsData, stageId) {
    const orderedLevelsData = Object.values(levelsData).filter(
        levelData => levelData.attributes.stage_id === stageId
    ).sort((firstLevel, secondLevel) => {
        return firstLevel.attributes.height - secondLevel.attributes.height;
    });
    const firstLevelId = orderedLevelsData[0]?.id;
    return firstLevelId;
}

export function hasElementsMatch(elementIds, matchsData, matchId) {
    if (!elementIds.length || !matchsData) return true
    for (const elementId of elementIds) {
        let matches = matchsData[elementId.toString()]
        if (matches && matches.includes(matchId)) {
            return true
        }
    }
    return false
}

export function cardsExist(cardsGroupData) {
    return typeof cardsGroupData.cards !== 'undefined'
}

export function getCategoryDefaultHeight(categoryData) {
    for (const categoryName of Object.keys(categoriesHeights)) {
        if (categoryData.attributes.name.includes(categoryName)) {
            let baseHeight = categoriesHeights[categoryName].base_height;
            let height = categoriesHeights[categoryName].height;
            return height - baseHeight;
        }
    }
    return 0;
}

export function findCardsGroupsRelatedTeams(cardsGroupIds, cardsGroupsData, teamsData) {
    const teamIds = [];
    for (const teamData of Object.values(teamsData)) {
        const protocolIds = Object.keys(teamData.protocols).map(id_ => parseInt(id_));
        const cardCategoryIds = Object.keys(teamData.card_categories).map(id_ => parseInt(id_));
        for (const cardsGroupId of cardsGroupIds) {
            if (!cardsGroupsData[cardsGroupId]) continue;
            const protocolId = cardsGroupsData[cardsGroupId].attributes.protocol_id;
            const cardCategoryId = cardsGroupsData[cardsGroupId].attributes.card_category_id;
            if (!protocolIds.includes(protocolId)) continue;
            if (cardCategoryIds.length && !cardCategoryIds.includes(cardCategoryId)) continue;
            teamIds.push(teamData.id);
            break;
        }
    }
    return teamIds;
}

export function findMatchingTeamsForCardsGroup(cardsGroupData, teamsData) {
    const matchingTeams = [];
    if (!cardsGroupData) return matchingTeams;
    const protocolId = cardsGroupData.attributes.protocol_id;
    const cardCategoryId = cardsGroupData.attributes.card_category_id;
    if (!protocolId || !cardCategoryId) return matchingTeams;
    for (const teamData of Object.values(teamsData)) {
        const protocolIds = Object.keys(teamData.protocols).map(id_ => parseInt(id_));
        const cardCategoryIds = Object.keys(teamData.card_categories).map(id_ => parseInt(id_));
        if (protocolIds.includes(protocolId) && cardCategoryIds.includes(cardCategoryId)) {
            matchingTeams.push(teamData);
        }
    }
    return matchingTeams;
}

export function findDuplicateTeamGroups(cardsGroupIds, cardsGroupsData, teamsData) {
    const autoAssign = {};
    const duplicates = {};
    for (const cardsGroupId of cardsGroupIds) {
        const cardsGroupData = cardsGroupsData[cardsGroupId];
        if (!cardsGroupData) continue;
        const matchingTeams = findMatchingTeamsForCardsGroup(cardsGroupData, teamsData);
        if (matchingTeams.length === 1) {
            autoAssign[cardsGroupId] = matchingTeams[0].id;
        } else if (matchingTeams.length > 1) {
            duplicates[cardsGroupId] = matchingTeams;
        }
    }
    return { autoAssign, duplicates };
}